import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  MouseEvent,
} from 'react'
import styled from 'styled-components'

import PlayCircleIcon from '../../icons/PlayCircle'

import InvisibleButton from '../InvisibleButton'

import {
  useForcePause,
  useLoadVideo,
  useVideoPlaying,
} from './playbackStateHooks'

const PlayToggle = styled(InvisibleButton)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const Figure = styled.figure`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${({ theme }) => theme['-ds-neutral-100'].value};
    width: 48px;
    height: 48px;
  }
`

const Video = styled.video<{ objectFit: 'contain' | 'cover' }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ objectFit = 'cover' }) => objectFit};
`

export type StreamableVideoHandle = {
  play: () => void
  pause: () => void
}

type StreamableVideoProps = {
  src: string
  poster: string
  forcePause?: boolean
  browserControls?: boolean
  objectFit?: 'contain' | 'cover'
  onPlay?: () => void
  fullscreenOnPlay?: boolean
}

type WebKitVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
}

const requestFullscreen = (video: WebKitVideoElement) => {
  // Refused where fullscreen isn't available (e.g. app webviews without
  // native support) — playback continues inline
  try {
    if (video.requestFullscreen) {
      video.requestFullscreen().catch(() => {})
    } else {
      // iPhone Safari only exposes fullscreen for video through this
      video.webkitEnterFullscreen?.()
    }
  } catch {
    // WebKit throws synchronously when the video isn't ready for fullscreen
  }
}

const StreamableVideo = forwardRef<StreamableVideoHandle, StreamableVideoProps>(
  (
    {
      src,
      poster,
      forcePause,
      browserControls,
      objectFit,
      onPlay,
      fullscreenOnPlay,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null)

    useLoadVideo(videoRef, src)

    useForcePause(videoRef, forcePause)

    useImperativeHandle(ref, () => ({
      play: () => {
        videoRef.current?.play()?.catch(() => {})
      },
      pause: () => {
        videoRef.current?.pause()
      },
    }))

    useEffect(() => {
      const video = videoRef.current
      if (!video) return undefined

      const handlePlay = () => {
        onPlay?.()
        if (fullscreenOnPlay) requestFullscreen(video)
      }

      video.addEventListener('play', handlePlay)
      return () => video.removeEventListener('play', handlePlay)
    }, [onPlay, fullscreenOnPlay])

    const videoPlaying = useVideoPlaying(videoRef)

    // Native controls stay off until first play: with a MediaSource attached
    // but nothing buffered, Chrome's controls draw a loading spinner over the
    // poster instead of a play button.
    const [hasPlayed, setHasPlayed] = useState(false)
    useEffect(() => {
      if (videoPlaying) setHasPlayed(true)
    }, [videoPlaying])
    const showBrowserControls = browserControls && hasPlayed

    const togglePlay = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const video = videoRef.current

      if (videoPlaying) {
        video?.pause()
      } else {
        video?.play()
      }
    }

    return (
      <Figure>
        {src && (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <Video
              ref={videoRef}
              poster={poster}
              controls={showBrowserControls}
              objectFit={objectFit}
              preload="none"
              playsInline
            />
            {!showBrowserControls && (
              <PlayToggle onClick={togglePlay} aria-label="Play video">
                {!videoPlaying && (
                  <IconWrapper>
                    <PlayCircleIcon />
                  </IconWrapper>
                )}
              </PlayToggle>
            )}
          </>
        )}
      </Figure>
    )
  },
)

export default StreamableVideo
